import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Tag, Popconfirm } from 'antd';
import TableHeaderDropdownSearch from '@/components/data-table/TableHeaderDropdownSearch';
import TableHeaderSearch from '@/components/data-table/TableHeaderSearch';
import TableHeaderSort from '@/components/data-table/TableHeaderSort';
import TableHeaderTitle from '@/components/data-table/TableHeaderTitle';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import {
  STATUS_COLORS,
  TASK_STATUSES,
  TASK_COLUMNS_DEFAULT_PARAMS,
} from '../../_shared/constants';
import { formatDuration } from '../../_shared/utils';

const STATUS_I18N_MAP = {
  OPEN: 'taskStatusOpen',
  IN_PROGRESS: 'taskStatusInProgress',
  DONE: 'taskStatusDone',
};

export const TaskColumns = ({
  canManage = false,
  onView,
  onEdit,
  onDelete,
  projectId,
}) => {
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const { t } = useTranslation(['tables', 'forms']);

  const statusOptions = useMemo(
    () =>
      Object.keys(TASK_STATUSES).map((key) => ({
        label: t(STATUS_I18N_MAP[key], { ns: 'forms' }),
        value: key,
      })),
    [t]
  );

  const [toggleDropdown, setToggleDropdown] = useState({
    name: false,
    status: false,
  });

  const [searchTerm, setSearchTerm] = useState(TASK_COLUMNS_DEFAULT_PARAMS);

  useEffect(() => {
    const status = queryParams?.status
      ? Array.isArray(queryParams.status)
        ? queryParams.status
        : [queryParams.status]
      : undefined;

    setSearchTerm({
      name: queryParams?.name || '',
      status,
    });
  }, [queryParams]);

  const handleClose = (name, value) => {
    setToggleDropdown((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchTerm = (name, value) => {
    setSearchTerm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchReset = (name) => {
    setQueryParams({ [name]: undefined });
    setSearchTerm((prev) => ({ ...prev, [name]: undefined }));
    handleClose(name, false);
  };

  const handleCheckboxReset = (name) => {
    setQueryParams({ [name]: undefined });
    setSearchTerm((prev) => ({ ...prev, [name]: [] }));
    handleClose(name, false);
  };

  const handleSubmit = (name, form) => {
    const value = form[name];
    handleClose(name, false);
    if (value && value?.length > 0) {
      setQueryParams({ [name]: value });
    } else {
      setQueryParams({ [name]: undefined });
    }
  };

  return [
    {
      title: () => (
        <TableHeaderTitle title={t('name', { ns: 'tables' })}>
          <TableHeaderSearch
            dataIndex="name"
            title={t('name', { ns: 'tables' })}
            searchTerm={searchTerm}
            onChange={handleSearchTerm}
            onSubmit={handleSubmit}
            onReset={handleSearchReset}
            toggleDropdown={toggleDropdown}
            onClose={handleClose}
            placement="bottomLeft"
          />
          <TableHeaderSort dataIndex="name" />
        </TableHeaderTitle>
      ),
      dataIndex: 'name',
      key: 'name',
      width: '35%',
    },
    {
      title: () => (
        <TableHeaderTitle title={t('status', { ns: 'tables' })}>
          <TableHeaderDropdownSearch
            dataIndex="status"
            title={t('status', { ns: 'tables' })}
            searchTerm={searchTerm}
            toggleDropdown={toggleDropdown}
            options={statusOptions}
            onChange={handleSearchTerm}
            onReset={handleCheckboxReset}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
          <TableHeaderSort dataIndex="status" />
        </TableHeaderTitle>
      ),
      dataIndex: 'status',
      key: 'status',
      width: '20%',
      render: (status) => (
        <Tag color={STATUS_COLORS[status]}>
          {t(STATUS_I18N_MAP[status], { ns: 'forms' })}
        </Tag>
      ),
    },
    {
      title: () => (
        <TableHeaderTitle title={t('estimatedHours', { ns: 'forms' })}>
          <TableHeaderSort dataIndex="estimatedHours" />
        </TableHeaderTitle>
      ),
      dataIndex: 'estimatedHours',
      key: 'estimatedHours',
      width: '15%',
      render: (val) => (val != null ? formatDuration(val * 60) : '-'),
    },
    ...(canManage
      ? [
          {
            title: t('billable', { ns: 'forms' }),
            dataIndex: 'billable',
            key: 'billable',
            render: (val) =>
              val == null
                ? '-'
                : val
                  ? t('yes', { ns: 'forms' })
                  : t('no', { ns: 'forms' }),
          },
        ]
      : []),
    {
      title: t('actions', { ns: 'tables' }),
      key: 'actions',
      width: canManage ? '9%' : '7%',
      render: (_, record) => (
        <div className="flex gap-1">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onView(record)}
          />
          {canManage && (
            <>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
              />
              <Popconfirm
                title={t('confirmDelete', { ns: 'forms' })}
                onConfirm={() => onDelete(projectId, record._id)}
              >
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </>
          )}
        </div>
      ),
    },
  ];
};
